import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const API = process.env.REACT_APP_BACKEND_URL;

const QUILL_FORMATS = [
  "header", "font", "size", "bold", "italic", "underline", "strike",
  "color", "background", "list", "align", "link", "image",
  "blockquote", "code-block", "video"
];

const AdminBlogManager = ({ token }) => {
  const [blogs, setBlogs] = useState([]);
  const [view, setView] = useState("list");
  const [editBlog, setEditBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [uploading, setUploading] = useState(false);
  const quillRef = useRef(null);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [author, setAuthor] = useState("Amatya");
  const [featuredImage, setFeaturedImage] = useState("");
  const [tags, setTags] = useState("");
  const [status, setStatus] = useState("draft");
  const [embedUrl, setEmbedUrl] = useState("");

  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  }), [token]);

  useEffect(() => { fetchBlogs(); }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/blogs`, { headers });
      const data = await res.json();
      setBlogs(data.blogs || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const sortedBlogs = useMemo(() => {
    return [...blogs].sort((a, b) => {
      const da = new Date(a.created_at || 0);
      const db2 = new Date(b.created_at || 0);
      return sortBy === "newest" ? db2 - da : da - db2;
    });
  }, [blogs, sortBy]);

  const resetForm = () => {
    setTitle(""); setContent(""); setExcerpt(""); setAuthor("Amatya");
    setFeaturedImage(""); setTags(""); setStatus("draft"); setEditBlog(null);
    setEmbedUrl("");
  };

  const openCreate = () => { resetForm(); setView("edit"); };

  const openEdit = (blog) => {
    setEditBlog(blog);
    setTitle(blog.title || "");
    setContent(blog.content || "");
    setExcerpt(blog.excerpt || "");
    setAuthor(blog.author || "Amatya");
    setFeaturedImage(blog.featured_image || "");
    setTags((blog.tags || []).join(", "));
    setStatus(blog.status || "draft");
    setView("edit");
  };

  // Image upload handler
  const handleImageUpload = useCallback(async (file, isFeatureImage = false) => {
    if (!file) return null;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/api/admin/blogs/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || "Upload failed");
        setUploading(false);
        return null;
      }
      const data = await res.json();
      const url = `${API}${data.url}`;
      if (isFeatureImage) {
        setFeaturedImage(url);
      }
      setUploading(false);
      return url;
    } catch (e) {
      alert("Upload failed: " + e.message);
      setUploading(false);
      return null;
    }
  }, [token]);

  // Quill image handler
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      const url = await handleImageUpload(file, false);
      if (url && quillRef.current) {
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, "image", url);
        editor.setSelection(range.index + 1);
      }
    };
  }, [handleImageUpload]);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        [{ font: [] }],
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "image", "video"],
        ["blockquote", "code-block"],
        ["clean"]
      ],
      handlers: { image: imageHandler }
    }
  }), [imageHandler]);

  // Insert embed (YouTube / Instagram)
  const insertEmbed = () => {
    const url = embedUrl.trim();
    if (!url) return;

    let embedHtml = "";

    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]+)/);
    if (ytMatch) {
      embedHtml = `<div class="embed-container" style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;max-width:100%;margin:16px 0;"><iframe src="https://www.youtube.com/embed/${ytMatch[1]}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allowfullscreen></iframe></div>`;
    }

    // Instagram
    const igMatch = url.match(/instagram\.com\/(p|reel|reels|tv)\/([\w-]+)/);
    if (igMatch) {
      embedHtml = `<div class="embed-container" style="max-width:540px;margin:16px auto;"><blockquote class="instagram-media" data-instgrm-permalink="${url}" data-instgrm-version="14" style="background:#FFF;border:0;border-radius:3px;box-shadow:0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15);margin:1px;max-width:540px;min-width:326px;padding:0;width:calc(100% - 2px);"><a href="${url}" target="_blank">View on Instagram</a></blockquote></div>`;
    }

    if (!embedHtml) {
      alert("Please paste a valid YouTube or Instagram URL");
      return;
    }

    setContent(prev => prev + embedHtml);
    setEmbedUrl("");
  };

  const handleSave = async () => {
    if (!title.trim()) { alert("Title is required"); return; }
    setSaving(true);
    const body = {
      title: title.trim(),
      content,
      excerpt: excerpt.trim(),
      author: author.trim(),
      featured_image: featuredImage.trim(),
      tags: tags.split(",").map(t => t.trim()).filter(Boolean),
      status
    };
    try {
      const url = editBlog
        ? `${API}/api/admin/blogs/${editBlog.id}`
        : `${API}/api/admin/blogs`;
      const method = editBlog ? "PUT" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (res.ok) {
        await fetchBlogs();
        setView("list");
        resetForm();
      } else {
        const err = await res.json();
        alert(err.detail || "Save failed");
      }
    } catch (e) { alert("Network error"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blog permanently?")) return;
    try {
      await fetch(`${API}/api/admin/blogs/${id}`, { method: "DELETE", headers });
      await fetchBlogs();
    } catch (e) { alert("Delete failed"); }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return "-"; }
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px", border: "1px solid #d4c5b0",
    borderRadius: 6, fontFamily: "Lato, sans-serif", fontSize: "0.95rem",
    background: "#fff", boxSizing: "border-box"
  };

  const labelStyle = {
    display: "block", fontFamily: "Lato, sans-serif", fontSize: "0.85rem",
    color: "#5a4a3a", fontWeight: 600, marginBottom: 6
  };

  // ===== EDIT VIEW =====
  if (view === "edit") {
    return (
      <div data-testid="blog-editor" style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ fontFamily: "Cinzel, serif", fontSize: "1.3rem", color: "#2E221A", margin: 0 }}>
            {editBlog ? "Edit Blog" : "New Blog"}
          </h2>
          <button
            data-testid="blog-back-btn"
            onClick={() => { setView("list"); resetForm(); }}
            style={{ padding: "8px 16px", background: "#f5efe8", border: "1px solid #d4c5b0", borderRadius: 6, cursor: "pointer", fontFamily: "Lato" }}
          >
            Back to List
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={labelStyle}>Title *</label>
            <input data-testid="blog-title-input" value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} placeholder="Blog title..." />
          </div>

          <div>
            <label style={labelStyle}>Excerpt (short description)</label>
            <textarea data-testid="blog-excerpt-input" value={excerpt} onChange={e => setExcerpt(e.target.value)} style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} placeholder="Brief summary (shown in blog listing)..." />
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 200px" }}>
              <label style={labelStyle}>Author</label>
              <input data-testid="blog-author-input" value={author} onChange={e => setAuthor(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <label style={labelStyle}>Status</label>
              <select data-testid="blog-status-select" value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          {/* Featured Image Upload */}
          <div>
            <label style={labelStyle}>Featured Image</label>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input
                data-testid="blog-image-input"
                value={featuredImage}
                onChange={e => setFeaturedImage(e.target.value)}
                style={{ ...inputStyle, flex: "1 1 300px" }}
                placeholder="Paste URL or upload image..."
              />
              <label
                data-testid="blog-image-upload-btn"
                style={{
                  padding: "10px 18px", background: "#8B4513", color: "#fff",
                  border: "none", borderRadius: 6, cursor: "pointer",
                  fontFamily: "Lato", fontSize: "0.9rem", whiteSpace: "nowrap",
                  opacity: uploading ? 0.6 : 1
                }}
              >
                {uploading ? "Uploading..." : "Upload Image"}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  disabled={uploading}
                  onChange={e => {
                    if (e.target.files[0]) handleImageUpload(e.target.files[0], true);
                  }}
                />
              </label>
            </div>
            {featuredImage && (
              <div style={{ marginTop: 8, borderRadius: 6, overflow: "hidden", maxWidth: 300 }}>
                <img src={featuredImage} alt="Preview" style={{ width: "100%", display: "block" }} onError={e => e.target.style.display = "none"} />
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>Tags (comma-separated)</label>
            <input data-testid="blog-tags-input" value={tags} onChange={e => setTags(e.target.value)} style={inputStyle} placeholder="Vedic astrology, kundli, yagya..." />
          </div>

          {/* Embed YouTube / Instagram */}
          <div>
            <label style={labelStyle}>Embed YouTube / Instagram Video</label>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input
                data-testid="blog-embed-input"
                value={embedUrl}
                onChange={e => setEmbedUrl(e.target.value)}
                style={{ ...inputStyle, flex: "1 1 300px" }}
                placeholder="Paste YouTube or Instagram URL..."
              />
              <button
                data-testid="blog-embed-btn"
                onClick={insertEmbed}
                style={{
                  padding: "10px 18px", background: "#2E221A", color: "#D4A843",
                  border: "none", borderRadius: 6, cursor: "pointer",
                  fontFamily: "Lato", fontSize: "0.9rem", whiteSpace: "nowrap"
                }}
              >
                Insert Embed
              </button>
            </div>
            <p style={{ fontFamily: "Lato", fontSize: "0.78rem", color: "#9a8572", margin: "6px 0 0" }}>
              Supports: YouTube videos/shorts, Instagram reels/posts
            </p>
          </div>

          {/* Content Editor */}
          <div>
            <label style={labelStyle}>Content *</label>
            <div style={{ background: "#fff", borderRadius: 6, border: "1px solid #d4c5b0" }}>
              <ReactQuill
                ref={quillRef}
                data-testid="blog-content-editor"
                theme="snow"
                value={content}
                onChange={setContent}
                modules={quillModules}
                formats={QUILL_FORMATS}
                placeholder="Write your blog content here... Hindi text (हिंदी) is fully supported. Use the image button in toolbar to upload images directly into content."
                style={{ minHeight: 300, fontFamily: "Lato, sans-serif" }}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 8 }}>
            <button
              data-testid="blog-save-draft-btn"
              onClick={() => { setStatus("draft"); setTimeout(handleSave, 0); }}
              disabled={saving}
              style={{
                padding: "10px 24px", background: "#f5efe8", color: "#5a4a3a",
                border: "1px solid #d4c5b0", borderRadius: 6, cursor: "pointer", fontFamily: "Lato", fontSize: "0.9rem"
              }}
            >
              Save as Draft
            </button>
            <button
              data-testid="blog-publish-btn"
              onClick={() => { setStatus("published"); setTimeout(handleSave, 0); }}
              disabled={saving}
              style={{
                padding: "10px 24px", background: "#8B4513", color: "#fff",
                border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "Lato", fontSize: "0.9rem"
              }}
            >
              {saving ? "Saving..." : (editBlog ? "Update & Publish" : "Publish")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== LIST VIEW =====
  return (
    <div data-testid="blog-manager" style={{ maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <h2 style={{ fontFamily: "Cinzel, serif", fontSize: "1.3rem", color: "#2E221A", margin: 0 }}>
          Blog Posts ({blogs.length})
        </h2>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <select
            data-testid="admin-blog-sort"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{ padding: "8px 12px", border: "1px solid #d4c5b0", borderRadius: 6, fontFamily: "Lato", fontSize: "0.85rem" }}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
          <button
            data-testid="blog-create-btn"
            onClick={openCreate}
            style={{
              padding: "8px 20px", background: "#8B4513", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "Lato", fontSize: "0.9rem"
            }}
          >
            + New Blog
          </button>
        </div>
      </div>

      {loading ? (
        <p style={{ color: "#725b47", fontFamily: "Lato" }}>Loading...</p>
      ) : sortedBlogs.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#725b47", fontFamily: "Lato" }}>
          No blogs yet. Click "New Blog" to create your first post.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sortedBlogs.map(blog => (
            <div key={blog.id} data-testid={`admin-blog-${blog.id}`} style={{
              background: "#fff", borderRadius: 8, padding: "14px 18px",
              border: "1px solid #e8ddd0", display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: 12, flexWrap: "wrap"
            }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontFamily: "Lato", fontSize: "0.95rem", color: "#2E221A", fontWeight: 600 }}>
                  {blog.title}
                </div>
                <div style={{ fontFamily: "Lato", fontSize: "0.8rem", color: "#9a8572", marginTop: 4 }}>
                  {formatDate(blog.created_at)} &middot;
                  <span style={{
                    padding: "2px 8px", borderRadius: 10, marginLeft: 6, fontSize: "0.75rem",
                    background: blog.status === "published" ? "#e6f4ea" : "#fff3e0",
                    color: blog.status === "published" ? "#2e7d32" : "#e65100"
                  }}>
                    {blog.status}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  data-testid={`edit-blog-${blog.id}`}
                  onClick={() => openEdit(blog)}
                  style={{ padding: "6px 14px", background: "#f5efe8", border: "1px solid #d4c5b0", borderRadius: 5, cursor: "pointer", fontFamily: "Lato", fontSize: "0.8rem" }}
                >
                  Edit
                </button>
                <button
                  data-testid={`delete-blog-${blog.id}`}
                  onClick={() => handleDelete(blog.id)}
                  style={{ padding: "6px 14px", background: "#fce4e4", border: "1px solid #e8b4b4", borderRadius: 5, cursor: "pointer", fontFamily: "Lato", fontSize: "0.8rem", color: "#c62828" }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBlogManager;
